/**
 * NotificationCell — Single notification row (icon + text only)
 *
 * Layout: [Avatar] [message + timeAgo] [chevron for friend_request]
 * Tapping emits 'tap' — parent decides what to do (open detail view, etc.)
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import type { GameNotification } from '@sdl/shared';

interface NotificationCellEvents {
  'tap': { notification: GameNotification };
  'dismiss': { notification: GameNotification };
  /** Gold gift: player tapped COLLECT. Carries the button for the coin flyout. */
  'collect': { notification: GameNotification; button: HTMLElement };
}

/** True for gifts that carry uncollected gold (Collect button, no X). */
export function isCollectableGift(n: GameNotification): boolean {
  return n.type === 'gift' && n.data.currency === 'gold';
}

export class NotificationCell extends EventEmitter<NotificationCellEvents> {
  public element: HTMLElement;
  private data: GameNotification;

  constructor(data: GameNotification) {
    super();
    this.data = data;
    this.element = this.createElement();
  }

  getData(): GameNotification {
    return this.data;
  }

  animateRemoval(): Promise<void> {
    return new Promise((resolve) => {
      this.element.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
      this.element.style.transform = 'translateX(100%)';
      this.element.style.opacity = '0';
      setTimeout(() => {
        this.element.remove();
        resolve();
      }, 300);
    });
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = `notif-cell${this.data.seen ? '' : ' notif-unseen'}`;
    el.addEventListener('click', () => {
      this.emit('tap', { notification: this.data });
    });
    this.render(el);
    return el;
  }

  private render(el: HTMLElement): void {
    const { fromUsername, fromPicture, createdAt } = this.data;
    const initial = fromUsername.charAt(0).toUpperCase();
    const avatarContent = fromPicture
      ? `<img src="${fromPicture}" alt="${initial}" class="notif-avatar-img">`
      : `<span class="notif-avatar-initial">${initial}</span>`;

    const timeStr = this.timeAgo(createdAt);
    let message = '';

    const collectable = isCollectableGift(this.data);

    if (this.data.type === 'friend_request') {
      message = `<strong>${fromUsername}</strong> wants to be your friend`;
    } else if (this.data.type === 'gift') {
      const amount = this.data.data.amount;
      message = collectable
        ? `<strong>${fromUsername}</strong> sent you a gift: <img class="notif-gold-icon" src="/assets/icons/currency/gold.svg" alt=""> <span class="notif-gold">${amount.toLocaleString()} Gold</span>`
        : `<strong>${fromUsername}</strong> sent you <span class="notif-stacks">${amount.toLocaleString()}</span> stacks`;
    } else if (this.data.type === 'system') {
      // Server-authored copy (trusted); rendered as-is.
      message = this.data.data.message;
    }

    // Uncollected gold gifts trade the X for a COLLECT button — collecting is
    // the only way to consume them (the server refuses to dismiss them too).
    const trailing = collectable
      ? `<button class="notif-collect-btn" data-sound="none">COLLECT</button>`
      : `<button class="notif-dismiss" aria-label="Dismiss">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>`;

    el.innerHTML = `
      <div class="notif-avatar">${avatarContent}</div>
      <div class="notif-body">
        <div class="notif-message">${message}</div>
        <div class="notif-time">${timeStr}</div>
      </div>
      ${trailing}
    `;

    // X dismisses this notification without triggering the row tap (detail view).
    el.querySelector('.notif-dismiss')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.emit('dismiss', { notification: this.data });
    });

    el.querySelector('.notif-collect-btn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.emit('collect', { notification: this.data, button: e.currentTarget as HTMLElement });
    });
  }

  private timeAgo(timestamp: number): string {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }
}
